import { Editor } from "tinymce";

import type { TextTemplateListItem } from "services/types/text-templates.type";

import type { TemplateSearcher } from "./use-template-search.hook";

// Sentinel value for the "Show more" row so onAction can tell it apart from a
// real template selection.
const SHOW_MORE_VALUE = "__suno_show_more__";

const MAX_BROWSE_RESULTS = 50;
const MAX_PREFETCH_TEMPLATES = 10;

/**
 * Registers the inline "/" template autocompleter on the editor.
 *
 * Browse (empty term) lists the pre-loaded, category-grouped templates. Typing a
 * term runs a server-side search so templates beyond the pre-loaded list's row
 * cap (the backend's 300-row limit) are still findable; a "Show more" row pages
 * through additional results instead of truncating to a single page (ENG-1426).
 * Kept in its own module so it can be unit-tested without pulling the editor
 * setup file's heavier import graph.
 */
export const handleTemplatesDropdown = (
  editor: Editor,
  textTemplates: TextTemplateListItem[],
  onTemplateInsert: (
    template: TextTemplateListItem,
    editor: Editor
  ) => Promise<void>,
  onPrefetchTemplateText?: (templateId: number) => void,
  searchTemplates?: TemplateSearcher
) => {
  const templateById = new Map<string, TextTemplateListItem>();

  // Register a template so onAction can resolve it for insertion — including
  // server search results that aren't in the pre-loaded browse list (ENG-1426).
  const toTemplateAction = (template: TextTemplateListItem) => {
    const templateId = String(template.id);
    templateById.set(templateId, template);
    return {
      type: "template",
      templateId,
      text: template.name,
      icon: "suno_templates"
    };
  };

  // Insert a category separator whenever the category changes, so the list is
  // grouped under category headers. The input must already be ordered by
  // category for the groups to be contiguous.
  const buildGroupedActions = (list: TextTemplateListItem[]): any[] => {
    const actions: any[] = [];
    let lastCategoryId: number | string | undefined = undefined;

    list.forEach((template) => {
      const currentCategoryId = template.category?.id;
      if (currentCategoryId !== lastCategoryId) {
        actions.push({
          type: "separator",
          text: template.category?.title || "No Category"
        });
        lastCategoryId = currentCategoryId;
      }
      actions.push(toTemplateAction(template));
    });

    return actions;
  };

  // Server search comes back ordered by name; sort by category (then name) so
  // the results group under the same category headers as the browse list.
  const sortByCategoryThenName = (
    list: TextTemplateListItem[]
  ): TextTemplateListItem[] =>
    [...list].sort((a, b) => {
      const categoryA = a.category?.title || "";
      const categoryB = b.category?.title || "";
      if (categoryA !== categoryB) {
        return categoryA.localeCompare(categoryB);
      }
      return (a.name || "").localeCompare(b.name || "");
    });

  const browseActions = buildGroupedActions(textTemplates);

  const prefetchTexts = (actions: any[]) => {
    if (!onPrefetchTemplateText) {
      return;
    }
    let prefetchCount = 0;
    for (const action of actions) {
      if (action.templateId && prefetchCount < MAX_PREFETCH_TEMPLATES) {
        const id = Number(action.templateId);
        if (!Number.isNaN(id)) {
          onPrefetchTemplateText(id);
          prefetchCount++;
        }
      }
    }
  };

  const toContents = (actions: any[]) =>
    actions.map((action: any) => ({
      text: action.text,
      icon: action.icon,
      value: action.templateId ?? action.text,
      type: action.type === "separator" ? "separator" : "autocompleteitem"
    }));

  // Browse: narrow the pre-loaded, category-grouped list client-side (an empty
  // term matches everything).
  const fetchBrowse = (lowerPattern: string) => {
    const matchedActions: any[] = [];
    let pendingSeparator: any = null;
    let itemCount = 0;

    for (
      let i = 0;
      i < browseActions.length && itemCount < MAX_BROWSE_RESULTS;
      i++
    ) {
      const action = browseActions[i];
      if (action.type === "separator") {
        pendingSeparator = action;
      } else if (action?.text?.toLowerCase().includes(lowerPattern)) {
        if (pendingSeparator) {
          matchedActions.push(pendingSeparator);
          pendingSeparator = null;
        }
        matchedActions.push(action);
        itemCount++;
      }
    }

    if (matchedActions.length) {
      matchedActions.unshift({
        type: "separator",
        text: "Type to search templates…"
      });
      prefetchTexts(matchedActions);
    }

    return toContents(matchedActions);
  };

  // Server search paging state. Accumulates across "Show more" so the dropdown
  // grows; a new search term resets it back to the first page.
  let searchTerm = "";
  let accumulated: TextTemplateListItem[] = [];

  const fetchSearch = async (term: string, loadMore: boolean) => {
    let hasMore: boolean;
    if (loadMore && term === searchTerm) {
      const page = await searchTemplates!.fetchPage(term, accumulated.length);
      accumulated = accumulated.concat(page.templates);
      hasMore = page.hasMore;
    } else {
      searchTerm = term;
      const page = await searchTemplates!.searchFirstPage(term);
      accumulated = page.templates;
      hasMore = page.hasMore;
    }

    const actions = buildGroupedActions(sortByCategoryThenName(accumulated));
    prefetchTexts(actions);

    const contents = toContents(actions);
    if (hasMore) {
      contents.push({
        text: "Show more…",
        value: SHOW_MORE_VALUE,
        type: "autocompleteitem",
        meta: { showMore: true }
      });
    }
    return contents;
  };

  editor.ui.registry.addAutocompleter("templatesDropdown", {
    trigger: "/",
    minChars: 0,
    columns: 1,
    maxResults: 500,
    fetch: async function (pattern, _maxResults, fetchOptions) {
      const trimmed = pattern.trim();

      if (!trimmed || !searchTemplates) {
        return fetchBrowse(trimmed.toLowerCase());
      }

      return fetchSearch(trimmed, Boolean(fetchOptions?.loadMore));
    },
    onAction: function (autocompleteApi, rng, value, meta) {
      // "Show more" loads the next page in place instead of inserting.
      if (meta?.showMore || value === SHOW_MORE_VALUE) {
        autocompleteApi.reload({ loadMore: true });
        return;
      }

      editor.selection.setRng(rng);
      editor.execCommand("Delete");
      autocompleteApi.hide();

      const template =
        typeof value === "string" ? templateById.get(value) : null;
      if (template) {
        void onTemplateInsert(template, editor);
      }
    }
  });
};
